/**
 * ============================================================
 * KEJJA OS — src/pages/library/LibraryLayout.tsx
 * Purple accent. Tabs: Shared | Personal.
 * ============================================================
 */

import { useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { BookOpen, Users, User, Search, Plus, Star } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ItemForm } from './ItemForm';
import ImportDocumentModal from './ImportDocumentModal';
import { Sparkles } from 'lucide-react';

export interface LibraryContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;
  scope: 'shared' | 'personal';
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  favoritesOnly: boolean;
  setFavoritesOnly: (v: boolean) => void;
  openNewItem: (hint?: { folderId?: string | null; ownerScope?: 'shared' | 'personal' }) => void;
  openEditItem: (itemId: string) => void;
}

export function useLibraryContext(): LibraryContext {
  return useOutletContext<LibraryContext>();
}

const TABS = [
  { to: '/library',          label: 'Shared',   icon: Users, end: true },
  { to: '/library/personal', label: 'Personal', icon: User },
];

export default function LibraryLayout() {
  const { data: homesData, isLoading: homesLoading } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';
  const currentUserId = undefined;

  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formHint, setFormHint] = useState<{ folderId?: string | null; ownerScope?: 'shared' | 'personal' } | null>(null);

  const openNewItem = (hint?: { folderId?: string | null; ownerScope?: 'shared' | 'personal' }) => {
    setEditingId(null);
    setFormHint(hint ?? null);
    setShowForm(true);
  };
  const openEditItem = (itemId: string) => {
    setEditingId(itemId);
    setFormHint(null);
    setShowForm(true);
  };

  if (homesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!homeId) return <div className="text-sm text-muted-foreground">No home found.</div>;

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const scope: 'shared' | 'personal' = pathname.endsWith('/personal') ? 'personal' : 'shared';

  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
                Library
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                <span className="italic">Wisdom in one place.</span>
                {homeName && <span className="ml-2">· {homeName}</span>}
              </p>
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            className="text-xs flex-shrink-0"
            onClick={() => setShowImport(true)}
          >
            <Sparkles className="w-3 h-3 mr-1 text-purple-600" /> Import
          </Button>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
            onClick={() => openNewItem({ ownerScope: scope })}
          >
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex items-center gap-1 overflow-x-auto">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search titles, body, tags…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Button
          size="sm"
          variant={favoritesOnly ? 'default' : 'outline'}
          className={cn('h-9 flex-shrink-0', favoritesOnly && 'bg-rose-600 hover:bg-rose-700 text-white')}
          onClick={() => setFavoritesOnly(!favoritesOnly)}
        >
          <Star className={cn('w-3.5 h-3.5', favoritesOnly && 'fill-current')} />
        </Button>
      </div>

      <Outlet context={{
        homeId, currentUserId, homeName,
        scope, searchQuery, setSearchQuery,
        favoritesOnly, setFavoritesOnly,
        openNewItem, openEditItem,
      } satisfies LibraryContext} />

      {showImport && (

        <ImportDocumentModal

          homeId={homeId}

          ownerScope={scope}

          onClose={() => setShowImport(false)}

        />

      )}

      {showForm && (
        <ItemForm
          homeId={homeId}
          itemId={editingId}
          hint={formHint}
          onClose={() => { setShowForm(false); setEditingId(null); setFormHint(null); }}
        />
      )}
    </div>
  );
}
